import React from 'react';
import { EditBase, useEditContext } from 'react-admin';
import { spinnerElement } from '../utils';

const Loaded = ({
    render,
    isComplete,
}: {
    render: (record: any) => React.ReactElement;
    isComplete?: (record: any) => boolean;
}) => {
    const { record, loading } = useEditContext();
    // antd only reads `initialValues` on the first render, so the form must not
    // mount until the record it is meant to be seeded with has arrived.
    if (loading || !record) return spinnerElement;
    if (isComplete && !isComplete(record)) return spinnerElement;
    return render(record);
};

export type EntityFormRouteProps = {
    /** present on the edit route, absent on create */
    id?: string;
    resource: string;
    basePath: string;
    /**
     * Whether the record in hand is the detail one.
     *
     * react-admin serves whatever the redux store already holds — which, for a
     * form reached from its list, is the record the *list* endpoint returned.
     * Where that payload is lighter than the detail one, the form would mount
     * on it, take its `initialValues`, and never see the fields that arrive
     * with the detail response a moment later. Say what makes a record whole
     * and the form waits for it.
     */
    isComplete?: (record: any) => boolean;
    render: (record?: any) => React.ReactElement;
};

/**
 * One component for both the create and the edit route: on edit it loads the
 * record and holds the form back until it is there, on create it renders the
 * same form with no record.
 */
const EntityFormRoute = ({ id, resource, basePath, isComplete, render }: EntityFormRouteProps) =>
    id ? (
        <EditBase id={id} resource={resource} basePath={basePath}>
            <Loaded render={render} isComplete={isComplete} />
        </EditBase>
    ) : (
        render(undefined)
    );

export default EntityFormRoute;
